import React, { lazy, useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import useIsMobile from "../js/utilities/useIsMobile";
import { useLanguage } from "../hooks/use-language";
import { useIsEnglishUrl } from "../hooks/is-english-url";
import { useTranslationsById } from "../hooks/use-translations-by-id";
import { Helmet } from "react-helmet";

const TitleContact = lazy(() => import('./componentes/TitleContact'));
const AppointmentSection = lazy(()=> import ('./componentes/Appointment'));
const RequestQuote = lazy (()=> import('../components/request-quote'));
const FeaturedCollection = lazy (()=> import ('./componentes/FeaturedCollectionSection'));
const Plans = lazy(()=> import('./componentes/Plans'));

/**
 * Página principal de contacto.
 *
 * Renderiza las diferentes secciones de la página de contacto:
 * - Encabezado principal.
 * - Colección destacada.
 * - Agendamiento de citas.
 * - Solicitud de cotización.
 * - Planes disponibles.
 *
 * También administra la navegación basada en rutas para los paneles
 * de contacto (cita o demo), sincronizando el estado interno con la URL
 * y realizando desplazamiento automático hacia la sección correspondiente
 * cuando la ruta coincide con alguno de los paneles configurados.
 *
 * Funcionalidades principales:
 * - Soporte para internacionalización (ES/EN).
 * - Navegación mediante React Router.
 * - Scroll automático hacia el contenido seleccionado.
 * - Carga diferida (lazy loading) de secciones para optimizar rendimiento.
 * - Adaptación de rutas según el idioma actual.
 *
 * @component
 * @returns {JSX.Element} Página de contacto.
 */
export const Contact = () => {
	const lang = useLanguage();
	const texts = useTranslationsById({ mainSectionName: "contacto", language: lang  });

	const history = useHistory();
	const location = useLocation();
	const sectionRef = useRef();
	const isEngUrl = useIsEnglishUrl();

	const tabPanelIds = {
		cita: `${isEngUrl ? "/en" : ""}/contacto/cita`,
		demo: `${isEngUrl ? "/en" : ""}/contacto/demo`,
	};

	const [currentTabPanel, setCurrentTabPanel] = useState("");

	const isMobile = useIsMobile();

	const tabPanelAriaLabel = {
		en: "Contact tabs panel",
		es: "Panel de tabs de contacto",
	};

	useEffect(() => {
		/**
			dependiendo de la ruta del navegador si coincide con algún id 
			de los tab panel, entonces muestra el panel respectivo, 
			si no es así mostrará por defecto la pagina de contacto
		 */
		if (Object.values(tabPanelIds).includes(location.pathname)) {
			setCurrentTabPanel(location.pathname);
			if (sectionRef?.current) {
				const element = sectionRef?.current;
				element.scrollIntoView({ behavior: "auto", block: "start" });
			}
		} else {
			setCurrentTabPanel("");
		}
	}, [location]);

	const tabPanelNavigateTo = (path) => {
		history.push(path);
	};

	return (
		<div className="contact-container">
			<Helmet>
				{/* Primary SEO */}
				<title>
					{texts.getText({
						id: "seo.title",
						defaultText:
							"Contacto MakeMake | Libros digitales para colegios y bibliotecas",
					})}
				</title>

				<meta
					name="description"
					content={texts.getText({
						id: "seo.description",
						defaultText:
							"Contacta con MakeMake para conocer nuestras soluciones de lectura digital para colegios, bibliotecas y docentes.",
					})}
				/>

				<link rel="canonical" href="https://www.makemake.com/contacto" />

				<meta name="robots" content="index, follow" />

				{/* Open Graph */}
				<meta property="og:type" content="website" />
				<meta
					property="og:title"
					content={texts.getText({
						id: "seo.title",
						defaultText:
							"Contacto MakeMake | Libros digitales para colegios y bibliotecas",
					})}
				/>
				<meta
					property="og:description"
					content={texts.getText({
						id: "seo.description",
						defaultText:
							"Contacta con MakeMake para conocer nuestras soluciones de lectura digital para colegios, bibliotecas y docentes.",
					})}
				/>
				<meta property="og:url" content="https://www.makemake.com/contacto" />
				<meta
					property="og:image"
					content="https://www.makemake.com/img/seo/contacto-og.jpg"
				/>

				{/* Twitter */}
				<meta name="twitter:card" content="summary_large_image" />
				<meta
					name="twitter:title"
					content={texts.getText({
						id: "seo.title",
						defaultText:
							"Contacto MakeMake | Libros digitales para colegios y bibliotecas",
					})}
				/>
				<meta
					name="twitter:description"
					content={texts.getText({
						id: "seo.description",
						defaultText:
							"Contacta con MakeMake para conocer nuestras soluciones de lectura digital para colegios, bibliotecas y docentes.",
					})}
				/>
				<meta
					name="twitter:image"
					content="https://www.makemake.com/img/seo/contacto-og.jpg"
				/>
			</Helmet>

			<TitleContact interfaceTexts={texts} />
			<FeaturedCollection interfaceTexts={texts}/>
			<AppointmentSection interfaceTexts={texts}/>
			<RequestQuote/>
			{/* <Plans interfaceTexts={texts}/> */}
		</div>
	);
};
